// src/pages/chat/ChatLayout.tsx
//
// Master-detail wrapper for /chats. The left pane is always the conversation
// list (Mattermost/Telegram-style); the right pane renders the active child
// route via <Outlet /> — empty state, new-chat, or a specific thread.

import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n } from '@react/hooks';
import type {
  Conversation,
  ConversationMember,
  Message,
  Presence,
  PresenceStatus,
  UnreadCount,
} from '@core/types/chat';
import { wsClient, WS_CHAT_ENABLED } from '@/features/chat/wsClient';
import {
  loadConversations,
  loadUnreadCounts,
  loadDirectConversationMembers,
  resolveConversationDisplay,
  getInitials,
  getAvatarColor,
  formatRelativeTime,
  getLastMessagePreview,
  filterConversations,
  sortByLastActivity,
  loadPresences,
  updateOwnPresence,
  presenceDotClass,
  presenceStatusOptions,
  type ChatFilter,
} from '@/features/chat/logic';
import { MessageSearchPanel } from '@/features/chat/MessageSearchPanel';

export const ChatLayout = observer(() => {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const { id: activeId } = useParams<{ id: string }>();
  const location = useLocation();
  const isNewChatRoute = location.pathname === '/chats/new' || location.pathname === '/chats/new/';

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [membersByConv, setMembersByConv] = useState<Map<string, ConversationMember[]>>(new Map());
  const [unreadMap, setUnreadMap] = useState<Map<string, UnreadCount>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ChatFilter>('all');

  // Presence (chat.UpdatePresence / GetPresence) — own status picker plus
  // colored dots on DM peers in the sidebar. Best-effort: never blocks or
  // errors the page, matches the ephemeral, Redis-only nature of presence
  // on the backend.
  const [ownStatus, setOwnStatus] = useState<PresenceStatus>('PRESENCE_STATUS_ONLINE');
  const [showPresenceMenu, setShowPresenceMenu] = useState(false);
  const [presenceMap, setPresenceMap] = useState<Map<string, Presence>>(new Map());
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      loadConversations(),
      loadUnreadCounts(),
    ])
      .then(async ([convs, unreadRes]) => {
        if (cancelled) return;
        const sorted = sortByLastActivity(convs);
        setConversations(sorted);
        if (unreadRes?.counts) {
          const map = new Map<string, UnreadCount>();
          for (const uc of unreadRes.counts) {
            if (uc.conversation?.id) map.set(uc.conversation.id, uc);
          }
          setUnreadMap(map);
        }
        const memberMap = await loadDirectConversationMembers(sorted);
        if (!cancelled) setMembersByConv(memberMap);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Own presence — set online as soon as the chat surface mounts, away
  // while the tab is hidden, back to online (or whatever the user last
  // picked manually) when it becomes visible again. UpdatePresence is
  // Redis-only on the backend (no Kafka, no DB row) — cheap to call often.
  useEffect(() => {
    updateOwnPresence('PRESENCE_STATUS_ONLINE');
    setOwnStatus('PRESENCE_STATUS_ONLINE');
    const handleVisibility = () => {
      if (document.hidden) {
        updateOwnPresence('PRESENCE_STATUS_AWAY');
      } else {
        updateOwnPresence(ownStatus === 'PRESENCE_STATUS_AWAY' ? 'PRESENCE_STATUS_ONLINE' : ownStatus);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      updateOwnPresence('PRESENCE_STATUS_OFFLINE');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetOwnStatus = (status: PresenceStatus) => {
    setOwnStatus(status);
    updateOwnPresence(status);
    setShowPresenceMenu(false);
  };

  // Peer presence for direct conversations — batch-fetch (chat.GetPresence)
  // whenever the DM peer set changes, then refresh on an interval so the
  // sidebar dots don't go stale during a long session.
  const dmPeerIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const conv of conversations) {
      if (conv.type !== 'CONVERSATION_TYPE_DIRECT') continue;
      const convId = conv.entity?.id;
      if (!convId) continue;
      const members = membersByConv.get(convId) ?? [];
      for (const m of members) {
        const pid = m.profile?.entity?.id;
        if (pid && pid !== auth.activeProfileId) ids.add(pid);
      }
    }
    return Array.from(ids).sort().join(',');
  }, [conversations, membersByConv, auth.activeProfileId]);

  useEffect(() => {
    if (!dmPeerIdsKey) return;
    const ids = dmPeerIdsKey.split(',');
    let cancelled = false;
    const refresh = () => {
      loadPresences(ids).then(map => { if (!cancelled) setPresenceMap(map); });
    };
    refresh();
    const interval = window.setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [dmPeerIdsKey]);

  // Live updates for the conversation sidebar — same pattern as
  // ChatListPage but mounted here too because /chats/:id renders this
  // layout (not ChatListPage), so without the subscription the sidebar
  // shows stale last_message + unread count until a manual refresh.
  // Per-conversation handler bumps the unread badge unless the sender
  // is the current user, refreshes last_message preview, re-sorts the
  // list newest-first, and zeroes the badge on local messages_read.
  const conversationIdsKey = conversations.map(c => c.entity?.id).filter(Boolean).join(',');
  useEffect(() => {
    if (!WS_CHAT_ENABLED) return;
    if (conversations.length === 0) return;
    const myProfileId = auth.activeProfileId ?? null;
    const unsubs: Array<() => void> = [];
    for (const conv of conversations) {
      const convId = conv.entity?.id;
      if (!convId) continue;
      unsubs.push(
        wsClient.subscribe('conv:' + convId, {
          onMessage: (msg: Message) => {
            if (!msg) return;
            setConversations(prev => {
              const next = prev.map(c => {
                if (c.entity?.id !== convId) return c;
                return { ...c, last_message: msg, last_activity_at: msg.created_at };
              });
              return sortByLastActivity(next);
            });
            const senderId = msg.sender?.entity?.id;
            if (senderId && senderId !== myProfileId) {
              setUnreadMap(prev => {
                const existing = prev.get(convId);
                const next = new Map(prev);
                next.set(convId, {
                  conversation: existing?.conversation ?? { id: convId } as any,
                  unread_count: (existing?.unread_count ?? 0) + 1,
                  unread_mentions: existing?.unread_mentions ?? 0,
                  last_message_at: msg.created_at,
                });
                return next;
              });
            }
          },
          onMessagesRead: (payload: { profile_id?: string }) => {
            if (!payload?.profile_id || payload.profile_id !== myProfileId) return;
            setUnreadMap(prev => {
              const existing = prev.get(convId);
              if (!existing) return prev;
              const next = new Map(prev);
              next.set(convId, { ...existing, unread_count: 0, unread_mentions: 0 });
              return next;
            });
          },
          onConversationUpdated: (updated: Conversation) => {
            if (!updated?.entity?.id) return;
            setConversations(prev =>
              prev.map(c => (c.entity?.id === updated.entity!.id ? { ...c, ...updated } : c)),
            );
          },
        }),
      );
    }
    return () => {
      for (const u of unsubs) u();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationIdsKey, auth.activeProfileId]);

  const filtered = useMemo(
    () => filterConversations(conversations, search, typeFilter),
    [conversations, search, typeFilter],
  );

  const filters: { key: ChatFilter; labelKey: string }[] = [
    { key: 'all', labelKey: 'common.all' },
    { key: 'direct', labelKey: 'chat.type.direct' },
    { key: 'group', labelKey: 'chat.type.group' },
    { key: 'channel', labelKey: 'chat.type.channel' },
  ];

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* ============ Left pane — conversation list ============ */}
      <aside className="flex w-80 flex-shrink-0 flex-col border-r border-border bg-muted/10">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="relative flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowPresenceMenu(v => !v)}
              className="flex items-center gap-1.5 rounded-md px-1 py-1 hover:bg-muted transition-colors"
              title={t('chat.presence.setStatus')}
            >
              <span className={`h-2 w-2 rounded-full ${presenceDotClass(ownStatus)}`} />
              <h1 className="text-base font-semibold text-foreground">{t('chat.title')}</h1>
            </button>
            {showPresenceMenu && (
              <div className="absolute left-0 top-full z-20 mt-1 w-44 rounded-lg border border-border bg-popover py-1 shadow-lg">
                {presenceStatusOptions.filter(o => o.value !== 'PRESENCE_STATUS_INVISIBLE').map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSetOwnStatus(opt.value as PresenceStatus)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted ${
                      ownStatus === opt.value ? 'font-semibold text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full ${opt.dotClass}`} />
                    {t(opt.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowGlobalSearch(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title={t('chat.search.title')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </button>
            <Link
              to="/chats/new"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title={t('chat.newConversation')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 7.125l-2.625-2.625" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="border-b border-border px-3 py-2">
          <div className="relative">
            <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('chat.searchPlaceholder')}
              className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
            />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex border-b border-border px-2">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setTypeFilter(f.key)}
              className={`flex-1 border-b-2 px-2 py-2 text-xs font-medium transition-colors -mb-px ${
                typeFilter === f.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-1 p-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 rounded-md p-2">
                  <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-40 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <ChatListEmptyState search={search} t={t} />
          ) : (
            <ul role="list" className="px-1 py-1">
              {filtered.map(conv => (
                <ConversationListItem
                  key={conv.entity?.id}
                  conversation={conv}
                  active={conv.entity?.id === activeId}
                  locale={locale}
                  unread={unreadMap.get(conv.entity?.id ?? '')}
                  members={membersByConv.get(conv.entity?.id ?? '')}
                  currentProfileId={auth.activeProfileId}
                  presenceMap={presenceMap}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* ============ Right pane — active thread / empty state ============ */}
      <section className="flex min-w-0 flex-1 flex-col">
        {activeId || isNewChatRoute ? (
          <Outlet />
        ) : (
          <ChatRightEmpty t={t} />
        )}
      </section>

      {/* Global message search (chat.SearchMessages, unscoped) — overlays
          the right pane as a slide-in drawer rather than a page nav, so
          the active conversation (if any) isn't lost underneath it. */}
      {showGlobalSearch && (
        <div className="fixed inset-y-0 right-0 top-16 z-30 shadow-xl">
          <MessageSearchPanel
            locale={locale}
            onClose={() => setShowGlobalSearch(false)}
            onJump={(conversationId, msgId) => {
              setShowGlobalSearch(false);
              navigate(`/chats/${conversationId}?m=${msgId}`);
            }}
          />
        </div>
      )}
    </div>
  );
});

// ----------------------------------------------------------------------------

function ConversationListItem({
  conversation,
  active,
  locale,
  unread,
  members,
  currentProfileId,
  presenceMap,
  t,
}: {
  conversation: Conversation;
  active: boolean;
  locale: string;
  unread?: UnreadCount;
  members?: ConversationMember[];
  currentProfileId: string | null;
  presenceMap?: Map<string, Presence>;
  t: (key: any, params?: any) => string;
}) {
  const id = conversation.entity?.id;
  const display = resolveConversationDisplay(conversation, currentProfileId, members);
  const isDirect = conversation.type === 'CONVERSATION_TYPE_DIRECT';
  const peerId = isDirect
    ? members?.find(m => m.profile?.entity?.id && m.profile.entity.id !== currentProfileId)?.profile?.entity?.id
    : undefined;
  const peerPresence = peerId ? presenceMap?.get(peerId) : undefined;
  const displayName = display.name.startsWith('chat.') ? t(display.name) : display.name;
  const preview = getLastMessagePreview(conversation);
  const previewText = preview.text
    ? preview.senderName
      ? `${preview.senderName}: ${preview.text}`
      : preview.text
    : preview.textKey
      ? t(preview.textKey)
      : '';
  const unreadCount = unread?.unread_count ?? 0;
  const unreadMentions = unread?.unread_mentions ?? 0;
  const isChannel = conversation.type === 'CONVERSATION_TYPE_CHANNEL';

  return (
    <li>
      <NavLink
        to={`/chats/${id}`}
        className={() =>
          `flex items-center gap-3 rounded-md px-2 py-2 transition-colors ${
            active ? 'bg-primary/10 text-foreground' : 'hover:bg-muted'
          }`
        }
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {display.avatarUrl ? (
            <img
              src={display.avatarUrl}
              alt={displayName}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: getAvatarColor(display.avatarColorSeed) }}
            >
              {isChannel ? '#' : getInitials(displayName)}
            </div>
          )}
          {peerPresence?.status && peerPresence.status !== 'PRESENCE_STATUS_OFFLINE' && (
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background ${presenceDotClass(peerPresence.status)}`}
            />
          )}
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-sm ${unreadCount > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
              {displayName}
            </span>
            {conversation.last_activity_at && (
              <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                {formatRelativeTime(conversation.last_activity_at, locale)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span
              className={`truncate text-xs ${
                unreadCount > 0 ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              {previewText}
            </span>
            {unreadCount > 0 && (
              <span
                className={`flex h-4 min-w-[16px] flex-shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ${
                  unreadMentions > 0 ? 'bg-destructive' : 'bg-primary'
                }`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        </div>
      </NavLink>
    </li>
  );
}

function ChatListEmptyState({
  search,
  t,
}: {
  search: string;
  t: (key: any, params?: any) => string;
}) {
  return (
    <div className="px-4 py-12 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
        </svg>
      </div>
      <p className="text-sm font-medium text-foreground">
        {search ? t('chat.noSearchResults') : t('chat.noItems')}
      </p>
      {!search && (
        <Link to="/chats/new" className="mt-3 inline-block text-xs font-semibold text-primary hover:underline">
          {t('chat.newConversation')}
        </Link>
      )}
    </div>
  );
}

function ChatRightEmpty({
  t,
}: {
  t: (key: any, params?: any) => string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-muted/5 p-10 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-foreground">{t('chat.emptyTitle')}</h2>
      <p className="max-w-sm text-sm text-muted-foreground">{t('chat.emptyDescription')}</p>
      <Link
        to="/chats/new"
        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        {t('chat.newConversation')}
      </Link>
    </div>
  );
}

export default ChatLayout;
